import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton
} from '@mui/material';
import {
  Add, Delete, Search, Description, Close, CloudUpload,
  Download, InsertDriveFile, PictureAsPdf, Image, Article
} from '@mui/icons-material';
import { documentsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const fileIcons = {
  pdf: <PictureAsPdf sx={{ fontSize: 20, color: '#ef4444' }} />,
  jpg: <Image sx={{ fontSize: 20, color: '#3b82f6' }} />,
  jpeg: <Image sx={{ fontSize: 20, color: '#3b82f6' }} />,
  png: <Image sx={{ fontSize: 20, color: '#3b82f6' }} />,
  doc: <Article sx={{ fontSize: 20, color: '#2563eb' }} />,
  docx: <Article sx={{ fontSize: 20, color: '#2563eb' }} />,
};

const categories = [
  { v: 'general', l: 'General' }, { v: 'contract', l: 'Contract' }, { v: 'license', l: 'License' },
  { v: 'insurance', l: 'Insurance' }, { v: 'tax', l: 'Tax' }, { v: 'report', l: 'Report' },
  { v: 'credential', l: 'Credential' }, { v: 'other', l: 'Other' }
];

const Documents = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'general', description: '', file: null });
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await documentsAPI.getAll({ limit: 1000 });
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Documents | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r =>
    [r.name, r.type, r.originalName].some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
  );

  const handleUpload = async () => {
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('type', form.category);
      fd.append('notes', form.description);
      if (form.file) {
        fd.append('file', form.file);
        fd.append('name', form.title || form.file.name);
      }
      await documentsAPI.upload(fd);
      setOpen(false); setForm({ title: '', category: 'general', description: '', file: null }); load();
      toast.success('Document uploaded successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Upload failed'); }
  };

  const handleDownload = async (id, filename) => {
    try {
      const res = await documentsAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename || 'download';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error('Download failed'); }
  };

  const handleDelete = async () => {
    try { await documentsAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Document deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const getExt = (name) => (name || '').split('.').pop().toLowerCase();
  const fmtSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Documents</Typography>
          <Typography variant="body2" color="text.secondary">Upload and manage business documents</Typography>
        </Box>
        <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setOpen(true)}
          sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', boxShadow: '0 4px 14px rgba(236,72,153,0.3)', px: 3, fontWeight: 700 }}>
          Upload Document
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <TextField size="small" placeholder="Search documents..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 320 }} />
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Document</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                          <Description sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No documents found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => {
                        const ext = getExt(r.originalName);
                        return (
                          <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#ec4899', 0.03) } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: alpha('#64748b', 0.08), borderRadius: 2 }}>
                                  {fileIcons[ext] || <InsertDriveFile sx={{ fontSize: 20, color: '#64748b' }} />}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.name || r.originalName}</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{r.originalName || r.name}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={categories.find(c => c.v === r.type)?.l || r.type || 'Other'} size="small" sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }} />
                            </TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{fmtSize(r.fileSize)}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</Typography></TableCell>
                            <TableCell align="right">
                              <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(r._id, r.originalName)} sx={{ color: '#3b82f6' }}><Download sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #ec4899, #db2777)', borderRadius: 2 }}>
              <CloudUpload sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography sx={{ fontWeight: 700 }}>Upload Document</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Title</Typography>
              <TextField fullWidth size="small" placeholder="Document title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Category</Typography>
              <TextField fullWidth size="small" select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {categories.map(c => <MenuItem key={c.v} value={c.v}>{c.l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Description</Typography>
              <TextField fullWidth size="small" multiline rows={2} placeholder="Optional description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>File</Typography>
              <input ref={fileRef} type="file" hidden onChange={e => setForm(f => ({ ...f, file: e.target.files[0] }))} />
              <Box onClick={() => fileRef.current?.click()} sx={{
                border: '2px dashed', borderColor: form.file ? '#10b981' : 'divider', borderRadius: 3,
                p: 3, textAlign: 'center', cursor: 'pointer',
                bgcolor: form.file ? alpha('#10b981', 0.04) : alpha('#64748b', 0.02),
                transition: 'all 0.2s', '&:hover': { borderColor: '#3b82f6', bgcolor: alpha('#3b82f6', 0.04) }
              }}>
                <CloudUpload sx={{ fontSize: 36, color: form.file ? '#10b981' : '#94a3b8', mb: 1 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: form.file ? '#059669' : 'text.secondary' }}>
                  {form.file ? form.file.name : 'Click to select a file'}
                </Typography>
                {form.file && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>{fmtSize(form.file.size)}</Typography>}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}
            sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', fontWeight: 700, px: 4 }}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Document?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This file will be permanently removed.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
